// Skarbiec (BANK-D V-5) mieszka w module BEZ zależności, żeby czytał go też
// test parytetu w node. Tutaj tylko re-eksport, żeby reszta kodu miała jedno
// znajome miejsce na stałe balansu.
export {
  VAULT_STORAGE,
  VAULT_STORAGE_COST,
  vaultCap,
  vaultCapFrom,
  type VaultCapConfig,
} from '../domain/economy/vaultCapacity';
// E-1b: magazyn jajek liczony w SLOTACH (100 sztuk jednego rodzaju = 1 slot).
// Arytmetyka mieszka w module bez zależności, żeby dało się ją importować także
// z testów node'owych — ten sam wzorzec co `vaultCapacity`. Re-eksport zostaje,
// bo pół gry importuje limity z `balance.config`.
export {
  EGG_STACK_SIZE,
  EGG_STORAGE,
  eggStorageSlots,
  eggSlotsForCount,
  eggSlotsUsed,
  eggSlotsAfterAdding,
  eggStorageMaxEggs,
} from '../domain/economy/eggSlots';
/**
 * BALANS GLOBALNY — JEDNO ŹRÓDŁO PRAWDY DLA WSZYSTKICH LICZB BALANSOWYCH.
 *
 * Tu mieszkają stałe i krzywe ekonomiczne. Pliki tematyczne (pets, eggs, biomes,
 * destructibles, upgrades) IMPORTUJĄ stąd wartości i funkcje, zamiast trzymać
 * własne magic numbers.
 *
 * Reguła kciuka: jeśli musisz coś przekręcić dla balansu, zmieniasz to TUTAJ.
 * Jeśli liczba jest specyficzna dla jednego peta/jajka/biomu — zostaje w
 * dotyczącym pliku.
 *
 * Każda sekcja zaczyna się komentarzem // ─── NAZWA ─── żeby łatwo skoczyć w IDE.
 */

import type { CurrencyId } from '@/domain/types/common';

// Sesja 30 POST review LOW (Codex E): `SAVE_VERSION` przeniesiony do
// `src/save/PlayerSave.schema.ts` (jedyna canonical lokalizacja). Stary eksport
// stąd usunięty — pozostaje pułapka gdyby wartości się rozjeżdżały (był = 1
// gdy schema = 2 po sesji 30). Runtime używa schema.ts, więc nic się nie psuło,
// ale teraz jednoznaczne.

// ─────────────────────────────── SLOTY PETÓW ──────────────────────────────────
/**
 * Ile petów gracz może mieć equipped (idą za nim w formacji i atakują obiekty).
 *
 * ⭐ ŻEBY ZWIĘKSZYĆ MAX LICZBĘ PETÓW W GRZE — zmień maxCount i ewentualnie dodaj
 * kolejne poziomy upgrade'u pet_slots w upgrades.config.ts.
 */
export const PET_SLOTS = {
  startCount: 3,        // ile slotów ma gracz na starcie
  maxCount: 6,          // twardy cap (też = max widocznych petów w formacji)
  formationRadius: 90,  // px — promień okręgu petów wokół gracza
} as const;

/**
 * Liczba slotów equipped przy danym poziomie upgrade'u `pet_slots`.
 *
 * Wzór: `startCount + level`, clamp do `maxCount`.
 *   level 0 → 3 sloty (startowe)
 *   level 1 → 4 sloty
 *   level 2 → 5 slotów
 *   level 3 → 6 slotów (max)
 *
 * Aby podnieść cap > 6 → zmień `PET_SLOTS.maxCount` (1 liczba, hard rule).
 */
export function equippedCap(level: number): number {
  return Math.min(PET_SLOTS.maxCount, PET_SLOTS.startCount + Math.max(0, level));
}

/**
 * Sloty pracy w banku (BANK-B, DESIGN_BANK.md §5.1) — start 2, +1/poziom
 * upgrade'u `work_slots`, max 6. MIRROR canonical `bank.config.json`
 * (work.slots) + serwerowego `workSlotsCap` w game-config/balance.ts.
 */
export const WORK_SLOTS = {
  startCount: 2,
  maxCount: 6,
} as const;

/** Liczba slotów pracy w banku przy poziomie upgrade'u `work_slots`. */
export function workSlotsCap(level: number): number {
  return Math.min(WORK_SLOTS.maxCount, WORK_SLOTS.startCount + Math.max(0, level));
}

// ─────────────────────────────── POJEMNOŚCI INVENTORY ─────────────────────────
/**
 * Pojemność inventory rośnie z upgrade'em. Wzór: base + perLevel * level.
 * Po przekroczeniu — gracz nie może wykluć / kupić więcej, zanim nie zrobi
 * miejsca (zmerge'uje, sprzeda).
 */
// PET_STORAGE bumped 2026-05-28: poprzednio 100..600 cap. Powód: starsza
// instancja gracza (test z synem) miała hardcoded cap 1000 i nazbierała 885
// petów. Migracja do nowej wersji wymaga, żeby level-0 cap zmieścił te 885 +
// headroom na dalszą grę. Nowy zakres: 1000..2000 (= MAX_INVENTORY w
// saves/schemas.ts — równo, bez bumpa schema).
export const PET_STORAGE = {
  base: 1000,
  perLevel: 100,
  maxLevel: 10,         // → max 2000 petów (koniec "złotego" tieru, płatny coinami)
} as const;

/**
 * Sesja event-engine — gem tier magazynu petów. Po zmaxowaniu `PET_STORAGE`
 * za coiny (level 10 = 2000 petów), gracz może kupować DALSZE poziomy WYŁĄCZNIE
 * za gemy. To celowy gem sink dla graczy z dużą nadwyżką gemów (farming
 * kryształów, boss rewards) — koszt rośnie stromo (base 1000 × 2.2^n), żeby
 * realnie "zjadać" duże stosy (np. gracz z 1M gemów domaxuje ~level 19/20).
 *
 * Poziomy liczone jako KONTYNUACJA `pet_storage` (level 11..maxLevel), nie
 * osobny upgrade — jeden pasek postępu w UpgradesModal, tylko waluta się
 * zmienia po przekroczeniu `startLevel`.
 *
 * SYNC z server/api/src/game-config/balance.ts → PET_STORAGE_GEM_TIER.
 */
export const PET_STORAGE_GEM_TIER = {
  startLevel: PET_STORAGE.maxLevel,  // od tego poziomu (10→11 wzwyż) płatność w gemach
  perLevel: 200,                     // większy skok niż złoty tier (+100) — "spore powiększenie"
  maxLevel: 20,                      // twardy cap całego pet_storage (2000 + 200*10 = 4000)
  baseCost: 1_000,                   // koszt levelu 11 w gemach
  costMult: 2.2,                     // 1000/2200/4840/... → cumulative ~2.2M za wszystkie 10 poziomów
} as const;

export function petStorageCap(level: number): number {
  const lvl = Math.max(0, level);
  if (lvl <= PET_STORAGE.maxLevel) {
    return PET_STORAGE.base + PET_STORAGE.perLevel * lvl;
  }
  const gemLevels = Math.min(lvl, PET_STORAGE_GEM_TIER.maxLevel) - PET_STORAGE.maxLevel;
  return PET_STORAGE.base + PET_STORAGE.perLevel * PET_STORAGE.maxLevel + PET_STORAGE_GEM_TIER.perLevel * gemLevels;
}
/**
 * Bulk hatch upgrade (5.D.3d) — „Wybuchowe wykluwanie".
 *
 * Per design doc §B/C: poziom upgrade'u → liczba jajek otwieranych w jednym
 * kliknięciu „Wykluj N×". Curve 1 → 2 → 4 → 8 → 12 (potęga 2 z wyjątkiem max,
 * który jest 12 zamiast 16 — żeby zmieścić mobile reveal grid 3×4).
 *
 * Level 0 = brak bulka (tylko single hatch). Bulk button pokazuje się od
 * level 1 (czyli po pierwszym zakupie).
 *
 * SYNC z server/api/src/game-config/balance.ts → `bulkHatchCount`.
 */
export const BULK_HATCH = {
  /** Mapowanie level → liczba jajek. Index = level. */
  countsPerLevel: [1, 2, 4, 8, 12] as const,
  maxLevel: 4,
} as const;

export function bulkHatchCount(level: number): number {
  const lvl = Math.max(0, Math.min(BULK_HATCH.maxLevel, Math.floor(level)));
  return BULK_HATCH.countsPerLevel[lvl] ?? 1;
}

// ─────────────────────────────── GRACZ ────────────────────────────────────────
export const PLAYER = {
  baseSpeed: 180,         // px/s — bazowa prędkość ruchu
  baseMagnetRange: 80,    // px — promień przyciągania monet
  hitboxRadius: 24,       // px — promień kolizji (nieużywany w MVP, na przyszłość)
} as const;

/** Mnożnik prędkości przy poziomie upgrade'u walk_speed (+8%/lvl). */
export function walkSpeedMult(level: number): number {
  return 1 + 0.08 * level;
}
/** Mnożnik zasięgu magnesu (+15%/lvl). */
export function magnetMult(level: number): number {
  return 1 + 0.15 * level;
}
/** Mnożnik zysku monet (+10%/lvl). */
export function coinGainMult(level: number): number {
  return 1 + 0.10 * level;
}
/** Mnożnik szczęścia w jajkach (+5%/lvl szansy na lepsze rarity — implementacja w eggRoll). */
export function eggLuckMult(level: number): number {
  return 1 + 0.05 * level;
}

// ─────────────────────────────── TICK / PERFORMANCE ──────────────────────────
export const COIN_MAGNET_DELAY_MS = 300;     // po ilu ms moneta zaczyna lecieć do gracza
export const TARGETING_TICK_MS = 150;        // co ile ms TargetingSystem rewybiera cele
export const AUTOSAVE_INTERVAL_MS = 10_000;  // co ile ms autosave w tle
export const PET_RETURN_SPEED_MULT = 1.8;    // pet wracający do formacji jest 1.8× szybszy

// ─────────────────────────────── PET — RUCH I WALKA ───────────────────────────
/**
 * Stałe sterujące zachowaniem petów na scenie. Per-pet damage = `PetDef.basePower * VARIANT_MULT`.
 */
export const PET = {
  baseSpeed: 280,             // px/s — bazowa prędkość peta (szybciej niż gracz, by nadążał)
  agroRange: 400,             // px od gracza — pety auto-atakują destructibles w tym promieniu
  attackRange: 40,            // px od destructibla — odległość uznawana za "w zasięgu ataku"
  attackIntervalMs: 500,      // co ile ms pet zadaje obrażenia
  slotReachThreshold: 16,     // px — gdy pet jest tak blisko slotu, wraca do IDLE_FOLLOW
  formationFollowLerp: 0.18,  // siła smooth-damp w IDLE_FOLLOW (0..1, większe = sztywniej za graczem)
} as const;

// ─────────────────────────────── COIN / DROP ──────────────────────────────────
export const COIN = {
  /** Maks liczba widocznych sprite'ów monet z jednej destrukcji. Faktyczna liczba = `coinSpriteCount()`. */
  maxSpritesPerDrop: 40,
  /** Minimalna liczba sprite'ów (nawet drobny drop = widać "shower"). */
  minSpritesPerDrop: 6,
  /** Rozrzut monet wokół zniszczonego obiektu (px). */
  scatterRadius: 80,
  /** Czas lotu monety do gracza po magnetowaniu (ms). */
  magnetTravelMs: 400,
  /** Dystans od gracza, na którym moneta jest zbierana (px). */
  pickupDistance: 24,
  /** Per-moneta losowy bonus magnet delay (ms) — daje cascade/waterfall feel. */
  magnetDelayJitterMs: 250,
} as const;

/**
 * Krzywa: ile sprite'ów coin spawnować dla danej wartości.
 * sqrt(total) × 2 — drobne dropy dają ~6, duże destrukcje ~30-40.
 * Przykłady:
 *   5 coins  → 6 sprites  (min cap)
 *   25       → 10
 *   100      → 20
 *   250      → 32
 *   800      → 40         (max cap)
 *   3000     → 40         (max cap)
 */
export function coinSpriteCount(totalCoins: number): number {
  const raw = Math.ceil(Math.sqrt(Math.max(0, totalCoins)) * 2);
  return Math.max(COIN.minSpritesPerDrop, Math.min(COIN.maxSpritesPerDrop, raw));
}

// ─────────────────────────────── WALUTY ───────────────────────────────────────
/**
 * Domyślny stan walut nowego gracza.
 */
export const STARTING_CURRENCIES: Record<CurrencyId, number> = {
  coins: 50,            // tyle żeby kupić 1 egg-starter (zostaje 0)
  gems: 0,
  event_hearts: 0,
};

// ─────────────────────────────── STARTOWY SEED GRACZA ─────────────────────────
/**
 * Co dostaje nowy gracz przy pierwszym uruchomieniu. Pierwsze 3 jajka są wręczone
 * w inventory jajek, żeby gracz miał coś do wyklucia w pierwsze sekundy gry
 * (dopamine hit + tutorial wykluwania).
 *
 * Po wykluciu tych 3 jajek gracz musi sam zarobić coins i kupić kolejne w sklepie.
 */
/**
 * 4 pety do wyboru przy first-run starter selector (sesja 30). Gracz wybiera 2,
 * dostaje je jako equipped + 1 bonus starter egg w inventory.
 *
 * Wszystkie 4 mają `basePower: 5` w `pets.config.ts` (starter tier consistency).
 */
export const STARTER_PET_OPTIONS = [
  'pet-meadow-axolotl',
  'pet-meadow-dog',
  'pet-meadow-corgi',
  'pet-meadow-cat',
] as const;

/** Ile petów gracz musi wybrać w starter selector (dokładnie 2 z 4). */
export const STARTER_PET_PICK_COUNT = 2;

export const STARTER_SEED = {
  // Sesja 30: zredukowane z 3 → 1 starter egg. Gracz dostaje 2 wybrane pety
  // equipped + 1 starter egg jako "edukacyjny bonus" (uczy hatch flow + daje
  // potencjalny 3-ci pet po wykluciu, wypełniając cap pet_slots=3).
  startingEggDefIds: ['egg-starter'] as const,
  startingBiomeId: 'biome-meadow' as const,
  startingUpgradeLevels: {
    pet_slots: 0,        // = 3 sloty (start)
    pet_storage: 0,      // = 1000 petów (po bumpie 2026-05-28, patrz PET_STORAGE)
    egg_storage: 0,      // = 12 slotów / do 1200 jajek
    walk_speed: 0,
    magnet_range: 0,
    coin_mult: 0,
    egg_luck: 0,
    bulk_hatch: 0,       // 5.D.3d — start: brak bulka (tylko single hatch)
  },
} as const;

// ─────────────────────────────── EKONOMIA JAJEK ───────────────────────────────
/**
 * Bazowe ceny jajek (mnożone przez `biome.eggCostMultiplier` z biomes.config.ts
 * gdy to samo jajko sprzedawane jest w wyższym tierze biomu — w MVP nieużywane).
 */
export const EGG_BASE_COSTS = {
  starter: 50,
  basic: 250,
  rare: 2_500,
  legendary: 25_000,
} as const;

/** Domyślny pity floor dla huge (gdy w EggDef nie podany własny). */
export const PITY_DEFAULTS = {
  hugeFloor: 500,        // co N jajek bez huge → gwarantowany huge
} as const;

/** Wymagania mergea. */
export const MERGE_RULES = {
  normalToGold: 10,      // 10 normal tego samego defId → 1 gold
  goldToRainbow: 12,     // 12 gold tego samego defId → 1 rainbow
  hugeMerge: null,       // huge w MVP nie merge'uje się
} as const;

// ─────────────────────────────── KRZYWA BIOMÓW ────────────────────────────────
/**
 * Wzór: cena_biomu(tier) = BIOME_UNLOCK_BASE * BIOME_UNLOCK_MULT^(tier-1).
 *
 * Z BASE=50_000 i MULT=15 dostajemy:
 *   tier 1 (Meadow):        0  (override w configu — startowy)
 *   tier 2 (Frozen):    50_000
 *   tier 3 (Volcano):  750_000
 *   tier 4 (Sky):   11_250_000
 *
 * Wymagana power skaluje się analogicznie.
 */
export const BIOME_UNLOCK_BASE = 50_000;
export const BIOME_UNLOCK_MULT = 15;
export const BIOME_POWER_BASE = 200;
export const BIOME_POWER_MULT = 10;

export function biomeUnlockCost(tier: number): string {
  if (tier <= 1) return '0';
  return (BigInt(BIOME_UNLOCK_BASE) * BigInt(BIOME_UNLOCK_MULT) ** BigInt(tier - 2)).toString();
}
export function biomePowerRequirement(tier: number): number {
  if (tier <= 1) return 0;
  return Math.round(BIOME_POWER_BASE * Math.pow(BIOME_POWER_MULT, tier - 2));
}

// ─────────────────────────────── KRZYWE UPGRADÓW ──────────────────────────────
/**
 * Generyczny wzór: cost(level) = baseCost * mult^level.
 * Konkretne baseCost/mult per upgrade definiuje upgrades.config.ts.
 */
export function upgradeCost(
  baseCost: number,
  mult: number,
  level: number,
): number {
  return Math.round(baseCost * Math.pow(mult, level));
}

/** Domyślne mnożniki kosztu dla typów upgrade'ów (do upgrades.config.ts). */
export const UPGRADE_COST_MULT = {
  walk_speed: 1.7,
  magnet_range: 1.6,
  coin_mult: 1.8,
  egg_luck: 2.0,
  pet_slots: 5.0,        // bardzo drogi — slot petów to duża rzecz
  pet_storage: 1.5,
  egg_storage: 1.5,
  bulk_hatch: 3.0,       // 5.D.3d — 4 levels = 3000/9000/27000/81000 (total 120k)
  vault_storage: 1.5,    // BANK-D V-5 — mnożnik gemowy; canonical: VAULT_STORAGE_COST
  work_slots: 8.0,       // BANK-B — 4 levels = 25k/200k/1.6M/12.8M (BALANS = OQ3)
} as const;

// ─────────────────────────────── EFEKTY OBJECT DESTROY ────────────────────────
/**
 * Reward z destructibles to TYLKO waluta. Bazowe wartości są w destructibles.config.ts;
 * tu trzymamy globalny mnożnik (np. event "x2 coins") oraz mnożnik per biom.
 */
export const GLOBAL_COIN_MULT = 1.0;      // event override podmienia w runtime

// ─────────────────────────────── ANIMACJE / UI ────────────────────────────────
export const EGG_HATCH_ANIM_MS = 1_500;          // czas animacji wykluwania
export const EGG_HATCH_SKIP_AFTER_MS = 500;      // po ilu ms można skipnąć
export const COIN_MAGNET_TRAVEL_MS = 400;        // czas lotu monety do gracza

// ─────────────────────────────── TELEPORT (portal między scenami) ─────────────
/**
 * Pokrętła widowiskowej animacji teleportu (`TeleportScene`) — odgrywana przy
 * zmianie biomu (mapa światów), wejściu na plac handlowy i powrocie z placu.
 *
 * Timeline:
 *   cover  → energia zlatuje się do środka, portal narasta i zakrywa ekran
 *   hold   → ekran w pełni zasłonięty; PODMIENIAMY scenę pod spodem
 *   reveal → portal się rozwiewa, odsłania nowy świat
 *
 * Suma cover+hold+reveal = całkowity czas „parosekundowego" przejścia.
 * Tweakuj tu, nie w kodzie sceny.
 */
export const TELEPORT = {
  coverMs: 850,        // narastanie portalu + zasłonięcie ekranu
  holdMs: 150,         // pełne zakrycie podczas podmiany sceny
  revealMs: 900,       // rozpłynięcie portalu, odsłonięcie nowego świata
  spinDegrees: 240,    // o ile portal obraca się podczas narastania
  ringShards: 12,      // ile świetlistych „odłamków" krąży wokół rdzenia
  gatherSparks: 22,    // ile iskier zlatuje się do środka (faza cover)
  burstSparks: 26,     // ile iskier wystrzela na zewnątrz (faza reveal)
} as const;

// ─────────────────────────────── ANIMATION — „Living Stickers" ────────────────
/**
 * Centralne pokrętła animacji proceduralnych. Wszystkie liczby tutaj.
 * Dotyczą petów, destructibles, monet, kamery, haptyki.
 *
 * Filozofia: pojedynczy PNG ma żyć przez idle bob + breath + tilt + squish + FX
 * (zamiast wymagać spritesheetów). Tweakuj tu, nie w kodzie animatorów.
 */
export const ANIMATION = {
  // ── Pet idle (gdy pet siedzi w slot'cie za graczem) ──────────────────────
  pet: {
    bobAmplitudePx: 4,           // jak wysoko pet się unosi (px)
    bobFrequencyHz: 1.2,         // ile razy na sekundę (1.2 Hz = ~1 bob co 0.83s)
    breathAmplitude: 0.04,       // skala oddychania (1.0 → 1.04 → 1.0)
    breathFrequencyHz: 0.8,
    blinkSparkleEverySecMin: 4,  // co ile sekund (losowo min..max) ✨ pulse
    blinkSparkleEverySecMax: 8,

    // Ruch
    maxTiltRad: 0.18,            // maks pochylenie sprite'a podczas ruchu (~10°)
    tiltLerp: 0.18,              // szybkość dochodzenia do tilt'u (smooth-damp)
    velocitySmoothing: 0.82,     // lowpass na velocity dla tilt'u (0..1, więcej = gładziej)
    movingBobMult: 1.6,          // bob jest szybszy i większy gdy pet się rusza

    // Atak (squash & stretch — krótki pulse przy uderzeniu)
    attackSquishX: 1.28,         // poziome rozciągnięcie (mocniejsze niż 1.18 — feel headbuttu)
    attackSquishY: 0.74,         // pionowe ściśnięcie
    attackReturnLerp: 0.22,      // jak szybko wraca do 1.0 (per frame)
    attackHitFlashMs: 60,        // czas białego flash'a — używany przez DestructibleAnimator (flash na CEL'u)

    // Lunge — mini-rzut peta w stronę targetu przy każdym hicie (headbutt feel)
    lungeDistancePx: 18,         // max przesunięcie sprite'a w stronę celu
    lungeDurationMs: 220,        // czas całego cyklu (wind-up + lunge + return)
    lungeWindupRatio: 0.18,      // jak długo trwa "cofnięcie" przed lunge (0..1 czasu)
    lungeRotationRad: 0.32,      // obrót w stronę celu na peak'u lunge'a
  },

  // ── Cień pod petem ───────────────────────────────────────────────────────
  shadow: {
    width: 36,
    height: 10,
    alpha: 0.25,
    offsetYpx: 22,               // jak nisko pod sprite'em (sprite ma 48px → 22)
  },

  // ── Destructible idle + death ────────────────────────────────────────────
  destructible: {
    idlePulseAmplitude: 0.02,    // delikatny scale-pulse w spoczynku
    idlePulseFrequencyHz: 0.5,
    idlePulsePhaseSpread: true,  // każdy destructible ma losową fazę (żeby nie pulsowały zgodnie)

    // Death animation
    deathScaleEnd: 0.0,
    deathRotationEnd: 0.6,       // rad — lekki spin
    deathDurationMs: 300,
    deathEase: 'Back.easeIn' as const,

    // Hit reaction (gdy obrażenia)
    hitShakePx: 3,
    hitShakeMs: 90,
  },

  // ── Moneta ───────────────────────────────────────────────────────────────
  coin: {
    spinFrequencyHz: 3,          // monety wirują w locie (fake 3D — scaleX puls)
    spinAmplitude: 0.6,          // od 1.0 do 0.4 i z powrotem
    pickupBurstScale: 1.4,       // gdy moneta dotyka gracza, pop scale
    pickupBurstMs: 120,
  },

  // ── FX globalne ──────────────────────────────────────────────────────────
  damagePopup: {
    riseDistance: 50,            // ile px popup leci do góry
    durationMs: 700,
    fontSizeMin: 14,             // px (małe obrażenia)
    fontSizeMax: 28,             // px (duże — np. od huge'a)
    fontSizeScaleAt: 100,        // przy jakim damage czcionka jest max
  },

  coinBurst: {
    particleCount: 12,
    particleLifeMs: 500,
    particleSpeedMin: 60,
    particleSpeedMax: 180,
    particleScaleStart: 0.8,
    particleScaleEnd: 0,
  },

  // ── Gracz — polish wizualny (bez bob'a, by nie trząść kamerą) ─────────────
  player: {
    // Hop punch — odbicie scale gdy gracz startuje ruch z miejsca
    hopOnStart: {
      scaleY: 1.14,                // peak — ile rozciągnięcia w pionie
      durationMs: 240,
    },
    // Stop squish — krótkie ściśnięcie pionowe gdy gracz przestaje się ruszać
    stopSquish: {
      scaleY: 0.92,                // peak — ile ściśnięcia
      durationMs: 200,
    },
    // Step sway — kołysanie scaleX w trakcie chodzenia (symuluje kroki)
    stepSwayFrequencyHz: 4.0,      // 4 kroki na sekundę — brisk walking
    stepSwayAmplitude: 0.025,      // amplituda (~2.5% wahania scaleX)
    movingSpeedThreshold: 30,      // px/s — powyżej tej prędkości jest "moving"

    // Dust trail — drobne cząstki kurzu z tyłu nogi gdy biegnie
    dustTrail: {
      speedThreshold: 110,         // px/s — powyżej której emitujemy kurz
      emitIntervalMs: 130,         // co ile ms spawn pojedynczej cząstki
      lifespanMs: 450,
      scaleStart: 0.7,
      scaleEnd: 0,
      tint: 0xC4B89E,              // ciepły kurzowy beż
      offsetBehindPx: 14,          // jak daleko za graczem (w kierunku przeciwnym do ruchu)
      gravityY: 60,
      depth: 4,                    // pod graczem (depth 6), nad destructibles (0)
    },

    // Idle fidget — po N sekundach bez ruchu, mini-pulse rozglądania
    idleFidget: {
      everySecMin: 5,
      everySecMax: 9,
      tiltRad: 0.12,               // delikatne pochył w bok (~7°)
      durationMs: 600,
    },
  },

  // ── Dekoracje biomu (kwiaty, krzaki, drzewka) ────────────────────────────
  /**
   * Idle sway + touch push.
   *  - Sway: każda dekoracja delikatnie się kołysze (sin t * amplitude) z losową fazą.
   *  - Touch: gdy gracz lub pet wchodzi w promień, dekoracja chyla się W KIERUNKU
   *    odsuwania się od entity (pseudo-fizyka odepchnięcia), wraca smooth-damp gdy
   *    entity odejdzie. Na pierwszym kontakcie spawnuje mały sparkle particle.
   */
  decoration: {
    // Domyślne wartości dla wszystkich dekoracji
    swayFrequencyHz: 0.4,            // jak szybko kołysze się (Hz)
    swayAmplitudeRadDefault: 0.04,   // amplituda kołysania (~2.3°)
    breathFrequencyHz: 0.7,
    breathAmplitude: 0.015,          // skala 1.0 ↔ 1.015 (subtle)

    // Touch detection
    touchPushRadius: 50,             // px — promień w którym entity wpływa na dekorację
    touchPushIntensityRad: 0.40,     // max kąt odchylenia (~23°) w peak'u kontaktu
    touchRecoverLerp: 0.18,          // jak szybko wraca do neutralnego po odejściu entity

    // Contact sparkle — szczegółowa konfiguracja particle przy pierwszym wejściu
    contactSparkle: {
      count: 10,                     // ↑ z 3 — żeby było wyraźnie widać
      lifespanMs: 800,               // ↑ z 400 — dłużej obecne na ekranie
      scaleStart: 1.4,               // ↑ z 0.55 — dużo większe cząstki (~22 px na peak'u)
      scaleEnd: 0,
      speedMin: 70,
      speedMax: 160,                 // ↑ z 70 — bardziej rozjeżdżają się w eksplozji
      gravityY: 60,
      angleMin: 230,                 // burst do góry (-y) ± 30°
      angleMax: 310,
      depth: 15,                     // ⭐ KLUCZ: nad dekoracją (depth -50), destructibles (0), pety (5), gracz (6)
    },

    // Per-textureKey overrides — kwiaty bujają się mocniej, krzaki delikatniej.
    // Dekoracje BEZ wpisu używają wartości domyślnych (fail-soft) — wpisy tylko
    // dla „bohaterskich" dekoracji, którym chcemy dać charakterystyczny ruch.
    perKey: {
      'deco_flower_pink':   { isFlower: true, swayAmplitudeRad: 0.07, contactTint: 0xff80b0 },
      'deco_flower_yellow': { isFlower: true, swayAmplitudeRad: 0.07, contactTint: 0xffe57f },
      'deco_bush':          { isFlower: false, swayAmplitudeRad: 0.025, contactTint: 0xa8e6a1 },
      // Ocean (biom 5) — rośliny dna bujają się MOCNIEJ niż kwiaty (prąd morski).
      'deco_ocean_kelp':     { isFlower: true, swayAmplitudeRad: 0.11, contactTint: 0x3fa66a },
      'deco_ocean_seagrass': { isFlower: true, swayAmplitudeRad: 0.09, contactTint: 0x5fb36a },
      'deco_ocean_anemone':  { isFlower: true, swayAmplitudeRad: 0.07, contactTint: 0xff8fb1 },
    } as Record<string, { isFlower: boolean; swayAmplitudeRad: number; contactTint: number }>,

    // Domyślny kolor dymków kontaktu PER BIOM (fallback po prefiksie textureKey, gdy
    // dekoracja nie ma wpisu w `perKey`). Atlantyda = podwodne biomy → lekko niebieskie
    // bąbelki zamiast białych. Sprawdzane PO `perKey` (perKey ma pierwszeństwo), PRZED
    // białym defaultem 0xffffff.
    contactTintByPrefix: {
      'deco_atlantis_': 0x9fd8ff,    // lekko niebieski — bąbelki głębin Atlantydy
    } as Record<string, number>,
  },

  // ── Destrukcja — preset'y FX per typ obiektu ─────────────────────────────
  /**
   * Każdy destructible ma `art.destroyFx` wskazujący preset. Preset definiuje:
   *  - main + secondary particle burst (2 warstwy cząstek)
   *  - flash (kolorowy radialny błysk wokół miejsca destrukcji)
   *  - shockwave (rozszerzający się ring, opcjonalny)
   *
   * Liczby cząstek są przemyślane — na średnim mobilnym GPU 16+16+ring = OK.
   */
  destruction: {
    presets: {
      coin: {
        main:    { textureKey: 'fx-particle-yellow', count: 14, lifespanMs: 550, speedMin:  80, speedMax: 200, gravityY: 220, scaleStart: 0.9, scaleEnd: 0 },
        accent:  { textureKey: 'fx-particle-white',  count:  6, lifespanMs: 350, speedMin:  40, speedMax: 100, gravityY:  60, scaleStart: 0.6, scaleEnd: 0 },
        flashColor: 0xfff4a0,       // ciepły blond błysk
        flashRadius: 70,
        flashDurationMs: 80,
        shockwave: null,
      },
      wood: {
        main:    { textureKey: 'fx-particle-brown',  count: 16, lifespanMs: 650, speedMin:  60, speedMax: 220, gravityY: 320, scaleStart: 0.8, scaleEnd: 0 },
        accent:  { textureKey: 'fx-particle-grey',   count: 10, lifespanMs: 500, speedMin:  30, speedMax:  80, gravityY:  40, scaleStart: 0.7, scaleEnd: 0 },
        flashColor: 0xffe8c0,
        flashRadius: 80,
        flashDurationMs: 90,
        shockwave: null,
      },
      stone: {
        main:    { textureKey: 'fx-particle-grey',   count: 18, lifespanMs: 750, speedMin:  70, speedMax: 240, gravityY: 380, scaleStart: 1.0, scaleEnd: 0 },
        accent:  { textureKey: 'fx-particle-white',  count: 14, lifespanMs: 600, speedMin:  40, speedMax: 140, gravityY:  60, scaleStart: 0.8, scaleEnd: 0 },
        flashColor: 0xfafafa,
        flashRadius: 95,
        flashDurationMs: 100,
        shockwave: { color: 0xffffff, maxRadius: 100, lineWidth: 4, durationMs: 380 },
      },
      crystal: {
        main:    { textureKey: 'fx-particle-cyan',   count: 22, lifespanMs: 900, speedMin: 100, speedMax: 300, gravityY: 160, scaleStart: 1.1, scaleEnd: 0 },
        accent:  { textureKey: 'fx-particle-white',  count: 16, lifespanMs: 700, speedMin:  60, speedMax: 180, gravityY:   0, scaleStart: 1.0, scaleEnd: 0 },
        flashColor: 0xa0f0ff,        // jasny cyjan
        flashRadius: 130,
        flashDurationMs: 160,
        shockwave: { color: 0x7fe0b0, maxRadius: 150, lineWidth: 5, durationMs: 500 },
      },
    },
  } as const,

  // ── Camera / haptyka ─────────────────────────────────────────────────────
  cameraShake: {
    // [intensity (0..1 of viewport), duration ms] per typ destructibla
    'dest-coin-pile-s':  { intensity: 0.003, durationMs:  80 },
    'dest-coin-pile-m':  { intensity: 0.005, durationMs: 120 },
    'dest-coin-pile-l':  { intensity: 0.008, durationMs: 180 },
    'dest-chest-wood':   { intensity: 0.006, durationMs: 150 },
    'dest-chest-stone':  { intensity: 0.010, durationMs: 220 },
    'dest-crystal':      { intensity: 0.015, durationMs: 350 },
  } as Record<string, { intensity: number; durationMs: number }>,

  haptics: {
    worldInteractionConfirmedMs: 12,
    petAttackMs: 12,
    coinPickupMs: 5,
    destructibleDestroyedMs: [40, 30, 60] as number[],   // wzorzec wibracji
  },
} as const;

// ─────────────────────────────── CELE CZASOWE (dokumentacja, nie używane w kodzie) ───
/**
 * Designerskie cele tempa gry. Trzymane jako liczby do sprawdzania przez skrypty
 * symulacyjne ("bot grający 1h").
 */
export const TIME_TARGETS = {
  firstEggHatchMs: 60_000,                   // pierwsza wykluwanie ≤ 60 s od startu
  firstLegendaryMs: 30 * 60_000,             // pierwsza legendary ≤ 30–60 min
  firstHugeMs: 4 * 60 * 60_000,              // pierwszy huge ≤ 4–8 h
  biome2UnlockMs: 60 * 60_000,               // odblokowanie 2-go biomu ≤ 60–90 min
} as const;

// ════════════════════════════════════════════════════════════════════════════
// TRADING PLAZA — Etap 5 (DESIGN_TRADING_PLAZA.md §8)
// ════════════════════════════════════════════════════════════════════════════
//
// FIXES3 (po Codex HIGH 1/2 review FIXES2): wartości NIE są zdefiniowane
// w tym pliku. Single source of truth = canonical JSON w backendzie
// (`server/api/src/game-config/trading-plaza.config.json`). Frontend pobiera
// runtime przez `GET /config/trading-plaza` → `useGameStore.tradingConfig`.
//
// Powód: poprzednia struktura (dwie kopie TS + smoke parity check) padała
// cross-platform z powodu native binary mismatch (tsx/esbuild Windows vs
// Linux). Canonical JSON eliminuje problem — single file, impossible to
// drift, zero TS transpile w smoke gate.
//
// Komponenty React/Phaser:
//   - Importuj TYPE: `import type { TradingPlazaConfig } from '@/config/balance.config'`
//     (re-export niżej, prawdziwa deklaracja w `trading-plaza.types.ts`)
//   - Czytaj WARTOŚCI z Zustand: `useGameStore(s => s.tradingConfig)`
//   - Jeśli `tradingConfig === null` → bootstrap broken (App się nie zamontuje),
//     więc komponent może bezpiecznie zakładać że jest dostępne
//
// Wyjątek: niska liczba absolutnie wymaganych przed-bootstrap stałych
// (np. limity walidacji formularzy) — dorzucić tu jako fallback z komentarzem.
// Reszta przez Zustand.
//
// Patrz DESIGN_TRADING_PLAZA.md §8.5 (rules updated po FIXES3).

export type { TradingPlazaConfig, StallLevel } from './trading-plaza.types';
